import React from "react";
import { PaymentCard, readableOnHex, scalarTokens } from "@outpave/ds";
import type { PaymentCardProps } from "@outpave/ds";
import { getLuminance } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useBrandingContext } from "context/BrandingContext";
import { useColorScheme } from "context/ColorSchemeContext";

const INK = {
  light: "var(--color-card-foreground)",
  dark: readableOnHex("#ffffff"),
};

export const isDarkInk = (mode?: string) => {
  if (!mode || mode === "light") return false;
  if (mode === "dark") return true;
  try {
    return getLuminance(mode) <= 0.5;
  } catch {
    return false;
  }
};

const GRADIENT = scalarTokens["gradient-brand"];
const IS_GRADIENT = /^(repeating-)?(linear|radial|conic)-gradient\(/i;

const asLayer = (value: string) =>
  IS_GRADIENT.test(value) || value.startsWith("url(")
    ? value
    : `url("${value}")`;

export type CardArt = "own" | "default" | "none";

/** The colour a card wears under any artwork. */
export type CardFace = "stock" | "brand" | "background";

export const CARD_FACES: CardFace[] = ["stock", "brand", "background"];

export const isCardFace = (value?: string): value is CardFace =>
  CARD_FACES.includes(value as CardFace);

/** Concrete hexes for the colour faces, since the ink is read off them. */
export interface CardFills {
  brand?: string;
  background?: string;
}

/** The brand fill and the canvas, which the package keeps the same in both schemes. */
export const cardFills = (tokens?: Record<string, string>): CardFills => ({
  brand: tokens?.["color-bg-brand"],
  background: tokens?.["color-bg-preview-dark"],
});

export interface CardBranding {
  customCardImage: string;
  platformCardImage: string;
  paymentCardArt?: string;
  paymentCardBackground?: string;
  paymentCardTextMode?: string;
  paymentCardFace?: string;
  fills?: CardFills;
}

export interface ResolvedFace {
  backgroundColor?: string;
  backgroundImage?: string;
  textColor?: string;
  backgroundSize: string;
}

type FaceOverrides = Pick<
  CompanyPaymentCardProps,
  "art" | "face" | "artwork" | "backgroundColor" | "backgroundImage"
>;

/** A chosen face under any artwork; over an image the ink is the stored choice, since an image cannot be judged. */
const resolveChosenFace = (
  face: CardFace,
  art: string,
  branding: CardBranding,
  storedInk: string,
): ResolvedFace => {
  if (face === "stock") {
    const design = asLayer(branding.platformCardImage);
    return {
      backgroundImage: art ? `${asLayer(art)}, ${design}` : design,
      textColor: storedInk,
      backgroundSize: art ? "contain, cover" : "cover",
    };
  }

  const fill = branding.fills?.[face];
  if (!fill) {
    return {
      backgroundImage: art ? `${asLayer(art)}, ${GRADIENT}` : GRADIENT,
      textColor: storedInk,
      backgroundSize: art ? "contain, cover" : "cover",
    };
  }
  return {
    backgroundColor: fill,
    backgroundImage: art ? asLayer(art) : undefined,
    textColor: art ? storedInk : readableOnHex(fill),
    backgroundSize: "contain",
  };
};

// A card design is the whole face: its edge is inset and its corners round, so
// anything painted under it showed as a rim.
const wholeDesign = (design: string, storedInk: string): ResolvedFace => ({
  backgroundImage: asLayer(design),
  textColor: storedInk,
  backgroundSize: "cover",
});

export const resolveCardFace = (
  overrides: FaceOverrides,
  branding: CardBranding,
): ResolvedFace => {
  const storedInk =
    INK[isDarkInk(branding.paymentCardTextMode) ? "dark" : "light"];
  if (overrides.backgroundImage) {
    return wholeDesign(overrides.backgroundImage, storedInk);
  }

  const upload = overrides.artwork || branding.customCardImage;
  const chosen =
    overrides.face !== undefined ? overrides.face : branding.paymentCardFace;
  if (isCardFace(chosen)) {
    const art = overrides.art === "none" ? "" : upload;
    return resolveChosenFace(chosen, art, branding, storedInk);
  }

  const { art: artMode, backgroundColor } = overrides;

  const mode =
    artMode ||
    branding.paymentCardArt ||
    (branding.customCardImage ? "own" : "default");

  if (mode === "default") {
    return wholeDesign(branding.platformCardImage, storedInk);
  }

  const art = mode === "own" ? upload : "";

  const ground = artMode
    ? backgroundColor
    : backgroundColor || branding.paymentCardBackground;

  const fit = mode === "own" ? "contain" : "cover";

  return {
    backgroundColor: ground,
    backgroundImage: art
      ? ground
        ? asLayer(art)
        : `${asLayer(art)}, ${GRADIENT}`
      : ground
        ? undefined
        : GRADIENT,
    textColor: !art && ground ? readableOnHex(ground) : storedInk,
    backgroundSize: art && !ground ? `${fit}, cover` : fit,
  };
};

export interface CompanyPaymentCardProps extends PaymentCardProps {
  /** Preview an unsaved artwork choice. */
  art?: CardArt;
  /** Preview an unsaved face; "" previews the older art and background rules. */
  face?: CardFace | "";
  /** The draft's colour-face fills; the live theme's otherwise. */
  fills?: CardFills;
  /** Preview an upload as the artwork, fresher than the branding record. */
  artwork?: string;
  /** Preview an unsaved ink mode. */
  textMode?: string;
}

const CompanyPaymentCard = React.forwardRef<
  HTMLDivElement,
  CompanyPaymentCardProps
>(
  (
    {
      art: artMode,
      face: faceOverride,
      fills,
      artwork,
      textMode,
      backgroundColor,
      backgroundImage,
      textColor,
      chip = false,
      ...rest
    },
    ref,
  ) => {
    const { logos, brandColors } = useBrandingContext();
    const theme = useTheme();
    const { colorScheme } = useColorScheme();

    const { sx: callerSx, ...forwarded } = rest;
    const face = resolveCardFace(
      {
        art: artMode,
        face: faceOverride,
        artwork,
        backgroundColor,
        backgroundImage,
      },
      {
        customCardImage: logos.customCardImage,
        platformCardImage: logos.platformCardImage,
        paymentCardArt: brandColors?.paymentCardArt,
        paymentCardBackground: brandColors?.paymentCardBackground,
        paymentCardTextMode: textMode ?? brandColors?.paymentCardTextMode,
        paymentCardFace: brandColors?.paymentCardFace,
        fills: fills ?? cardFills(theme.outpave?.tokens?.[colorScheme]),
      },
    );

    const { backgroundSize, ...paint } = face;
    const single = backgroundSize === "cover" || backgroundSize === "contain";

    return (
      <PaymentCard
        ref={ref}
        {...paint}
        textColor={textColor || face.textColor}
        chip={chip}
        backgroundSize={single ? backgroundSize : undefined}
        sx={
          single
            ? callerSx
            : [
                { backgroundSize },
                ...(Array.isArray(callerSx) ? callerSx : [callerSx]),
              ]
        }
        {...forwarded}
      />
    );
  },
);

CompanyPaymentCard.displayName = "CompanyPaymentCard";

export default CompanyPaymentCard;
